import { clockForTimezone } from '../../../../../../platform/util/clockSystem';
import CalendarClockLabel from '../../../../../../platform/util/CalendarClockLabel';
import React from 'react';
import { addDays, format, isBefore, parse, startOfDay, parseISO, endOfMonth } from 'date-fns';
import { getBookingWeekStart } from './bookingWeek';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { useQuery } from '@apollo/client';
import { useCalendar } from '../calendarContext';
import { BookingTypeStatus, useIsMobile } from '../../../../../../platform/util/utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { alpha } from "@mui/material/styles";
import { Button, Typography } from '@mui/material';
import { GET_BOOKING_BY_BOOKING_TYPE_ID, GET_STAFF_WORKING_HOUR } from '../../../../../../platform/graphql/queries';
import { calendarMonthRange } from '../../../../../../platform/util/calendarSystem';
import { resolveTimeZone } from '../../../../../../platform/util/timezoneUtils';

const BookingTimeSlotWrapper = ({ children, isTimeSlotVisible = false, bookingType, selectedStaffId = null, userTimezone, viewType, businessUrl }) => {
    const { selectedDate, setSelectedDate, displayCalendar, displayClock: preferredClock } = useCalendar();
    const timeZone = resolveTimeZone(userTimezone);
    const displayClock = clockForTimezone(preferredClock, timeZone)
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile();
    // Step 1: Treat `selectedDate` as local time
    const localStartDate = startOfDay(selectedDate);

    const weekStartDate = getBookingWeekStart(localStartDate);

    // Extract staff IDs from the business
    const staffIds = bookingType?.assignedStaffs?.map((staff) => staff.staffId) || [];
    // Availability covers a whole period. Selecting another day in that period
    // must not trigger an identical month/week request with a different date key.
    const startDate = viewType === 'week'
        ? format(weekStartDate, 'yyyy-MM-dd')
        : calendarMonthRange(format(localStartDate, 'yyyy-MM-dd'), displayCalendar || 'gregory').start;

    const { data: availability, loading, error } = useQuery(GET_STAFF_WORKING_HOUR, {
        variables: {
            staffIds: selectedStaffId === "All" || !selectedStaffId ? staffIds : [selectedStaffId],
            bookingTypeId: bookingType?.id || "1",
            startDate,
            viewType: viewType,
            viewCalendar: displayCalendar || 'gregory',
            timezone: timeZone
        },
        skip: staffIds.length === 0,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
        notifyOnNetworkStatusChange: true,
    });


    const { data: bookingData, loading: bookingLoading, error: bookingError } = useQuery(GET_BOOKING_BY_BOOKING_TYPE_ID, {
        variables: {
            bookingTypeId: Number(bookingType?.id),
        },
        skip: !bookingType?.id,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
        notifyOnNetworkStatusChange: true,
    });

    // Read the current query result directly; copying it to state adds a stale render.
    const workingHoursData = availability;
    const openings = getOpeningsForMonth();

    if (loading || bookingLoading) return <p role="status">Loading available times…</p>;
    if (error || bookingError) return <p role="alert">We couldn’t load available times. Please refresh the page and try again.</p>;



    const weekEndDate = addDays(weekStartDate, 6);
    const getBookingSortTimeMs = (booking) => {
        const primaryTime = new Date(booking?.time);
        if (!Number.isNaN(primaryTime.getTime())) return primaryTime.getTime();
        const createdAt = new Date(booking?.createdAt);
        if (!Number.isNaN(createdAt.getTime())) return createdAt.getTime();
        return 0;
    };

    const pickRoundRobinStaffId = (eligibleStaffIds = []) => {
        const normalizedEligibleIds = eligibleStaffIds
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0);
        if (!normalizedEligibleIds.length) return null;

        const assignedOrder = (bookingType?.assignedStaffs || [])
            .map((staff) => Number(staff?.staffId))
            .filter((id) => normalizedEligibleIds.includes(id));
        const orderedEligibleIds = assignedOrder.length ? assignedOrder : normalizedEligibleIds;

        if (orderedEligibleIds.length === 1) {
            return orderedEligibleIds[0];
        }

        const historicalBookings = (bookingData?.getBookingByBookingTypeId || [])
            .filter((booking) => Number(booking?.status) !== Number(BookingTypeStatus.DELETED))
            .filter((booking) => orderedEligibleIds.includes(Number(booking?.staffId || 0)))
            .sort((left, right) => getBookingSortTimeMs(left) - getBookingSortTimeMs(right));

        if (!historicalBookings.length) {
            return orderedEligibleIds[0];
        }

        const lastAssignedStaffId = Number(
            historicalBookings[historicalBookings.length - 1]?.staffId || 0
        );
        const lastAssignedIndex = orderedEligibleIds.indexOf(lastAssignedStaffId);

        if (lastAssignedIndex === -1) {
            return orderedEligibleIds[0];
        }

        return orderedEligibleIds[(lastAssignedIndex + 1) % orderedEligibleIds.length];
    };

    const daysOfWeek = [];
    let currentDatePointer = new Date(weekStartDate);

    const RenderButtons = ({ slots, handleTimeSlotClick, day }) => {
        if (!slots.length) return <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1', py: 2 }}>No available times on this date. Choose another date.</Typography>;
        return slots.map((slot) => createTimeSlotButton(slot, handleTimeSlotClick, day));
    };

    const NoSlotsMessage = ({ message = "No slots available.", style = {} }) => {
        return (
            <p
                style={{
                    color: "#d32f2f", // Use a red color for better visibility
                    fontSize: ".75rem",
                    textAlign: "center",
                    margin: "2px 0",  // Increased margin for better spacing
                    fontWeight: "600", // Slightly bolder text for emphasis
                    lineHeight: "1.5", // Increase line height for better readability
                    ...style, // Allow custom styles to be passed in
                }}
            >
                {message}
            </p>
        );
    };

    const createTimeSlotButton = (slot, handleTimeSlotClick, day) => {
        const isDisabled = isSlotDisabled(slot.time, day, timeZone);
        const isSlotAvailable = slot.remainingSlots > 0;
        const { remainingSlots, showRemaining } = slot;
        const isAssigneStaffAvaiable = slot.assignedStaffs?.length > 0
        return (
            <Button
                key={slot.time}
                color="primary"
                sx={{
                    height: "auto",
                    minHeight: displayClock === 'both' ? 64 : displayClock === 'ethiopian' || viewType === 'week' ? 52 : 44,
                    textTransform: 'none',
                    fontSize: "0.75rem",
                    borderRadius: "4px",
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.07),
                    border: 1,
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                    "&:hover": { bgcolor: "action.selected", borderColor: "primary.main" },
                    "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                    "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "text.disabled", borderColor: "divider" },
                    color: "primary.main",
                    pointerEvents: isDisabled || !isSlotAvailable ? "none" : "auto",
                    opacity: 1,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    padding: viewType === "week" ? "8px 2px" : "8px 10px",
                }}
                disabled={isDisabled || !isSlotAvailable}
                onClick={() => handleTimeSlotClick(slot.time, day)}
            >
                <CalendarClockLabel value={slot.time} clock={displayClock} align="center" compact={viewType === "week"} />

                {showRemaining && !isDisabled && isSlotAvailable && (
                    <Typography
                        sx={{
                            fontSize: "0.6rem",
                            color: "inherit",
                            marginTop: "1px",
                            textTransform: 'lowercase',
                        }}
                    >
                        Available: {remainingSlots} {/* Only show remaining slots if showRemaining is true */}
                    </Typography>
                )}

                {/* If showRemaining is true, display "No slots available" only if remainingSlots is 0 */}
                {remainingSlots === 0 && !isAssigneStaffAvaiable && !isDisabled && (
                    <NoSlotsMessage />
                )}
            </Button>

        );
    };

    const getDayAbbreviation = (date) => format(date, "EEE").toUpperCase();
    while (currentDatePointer <= weekEndDate) {
        daysOfWeek.push({
            date: new Date(currentDatePointer),
            abbreviation: getDayAbbreviation(currentDatePointer),
        });
        currentDatePointer = addDays(currentDatePointer, 1);
    }

    const formatTimeFromSlotPayload = (slotTime, userTimeZone, forceLocalClock = false) => {
        try { 
            const raw = String(slotTime || "").trim();
            if (!raw) return "";

            // Convert only when payload has explicit offset/UTC marker.
            const hasExplicitOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(raw);
            if (hasExplicitOffset && !forceLocalClock) {
                const parsedWithOffset = parseISO(raw);
                if (!Number.isNaN(parsedWithOffset.getTime())) {
                    return formatInTimeZone(parsedWithOffset, userTimeZone, 'h:mma').toLowerCase();
                }
            }

            // Otherwise treat as already-local clock time.
            const timePart = raw.match(/T(\d{2}):(\d{2})/);
            if (timePart) {
                const hour24 = Number(timePart[1]);
                const minute = Number(timePart[2]);
                const hour12 = hour24 % 12 || 12;
                const suffix = hour24 >= 12 ? "pm" : "am";
                return `${hour12}:${String(minute).padStart(2, "0")}${suffix}`;
            }

            const parsed = parseISO(raw);
            if (!Number.isNaN(parsed.getTime())) {
                return format(parsed, 'h:mma').toLowerCase();
            }
            return raw;
        } catch (error) {
            console.error('Error converting timezone:', error);
            return String(slotTime || "");
        }
    }

    const toUtcFromLocalDayAndTime = (dateString, localTimeLabel, tz) => {
        try {
            const parsedTime = parse(String(localTimeLabel || ""), "h:mma", new Date());
            if (Number.isNaN(parsedTime.getTime())) return null;
            const localDateTime = new Date(
                Number(String(dateString).slice(0, 4)),
                Number(String(dateString).slice(5, 7)) - 1,
                Number(String(dateString).slice(8, 10)),
                parsedTime.getHours(),
                parsedTime.getMinutes(),
                0,
                0
            );
            return zonedTimeToUtc(localDateTime, tz);
        } catch (_error) {
            return null;
        }
    };

    const hasSlotConflict = ({
        slotStartUtc,
        slotDurationMin,
        targetStaffId,
        bookings,
    }) => {
        if (!slotStartUtc || Number.isNaN(slotStartUtc.getTime())) return false;
        const slotStartMs = slotStartUtc.getTime();
        const slotEndMs = slotStartMs + Math.max(1, Number(slotDurationMin || 0)) * 60 * 1000;

        const relevantBookings = (bookings || []).filter((booking) => {
            if (Number(booking?.status) === Number(BookingTypeStatus.DELETED)) return false;
            if (!targetStaffId) return true;
            const bookingStaffId = Number(booking?.staffId || 0);
            if (!bookingStaffId) return true; // fallback conservative when staff id missing
            return bookingStaffId === Number(targetStaffId);
        });

        return relevantBookings.some((booking) => {
            const bookingStart = new Date(booking?.time);
            if (Number.isNaN(bookingStart.getTime())) return false;
            const durationMin = Math.max(
                1,
                Number(booking?.duration || bookingType?.length || 0)
            );
            const bookingStartMs = bookingStart.getTime();
            const bookingEndMs = bookingStartMs + durationMin * 60 * 1000;

            return slotStartMs < bookingEndMs && slotEndMs > bookingStartMs;
        });
    };

    const inferForceLocalClockForDay = (dayAvailability, userTimeZone) => {
        if (!dayAvailability?.timeSlots?.length) return false;
        if (slotZMode === "local") return true;
        if (slotZMode === "utc") return false;

        // auto mode:
        // If all slots are Z timestamps and, for negative offsets (Americas),
        // most slot hours are late-day UTC (>=16) with no early UTC times,
        // backend likely encoded local wall clock with a fake "Z".
        const slots = dayAvailability.timeSlots;
        const zSlots = slots.filter((s) => /Z$/i.test(String(s?.time || "")));
        if (zSlots.length !== slots.length) return false;

        const sampleOffset = formatInTimeZone(
            new Date(`${dayAvailability.date}T12:00:00.000Z`),
            userTimeZone,
            "XXX"
        );
        const offsetMatch = String(sampleOffset).match(/^([+-])(\d{2}):(\d{2})$/);
        if (!offsetMatch) return false;
        const sign = offsetMatch[1] === "-" ? -1 : 1;
        const offsetHours = sign * Number(offsetMatch[2]);
        if (offsetHours > -3) return false;

        const hours = zSlots
            .map((s) => String(s.time).match(/T(\d{2}):\d{2}/))
            .filter(Boolean)
            .map((m) => Number(m[1]));
        const lateUtcCount = hours.filter((h) => h >= 16).length;
        const earlyUtcCount = hours.filter((h) => h <= 6).length;
        return lateUtcCount >= 3 && earlyUtcCount === 0;
    };

    function getOpeningsForMonth() {
        return (workingHoursData?.getStaffAvailability?.availability || []).map(day => ({
            date: day.date,
            available: day.timeSlots?.some(slot => new Date(slot.time) > new Date()) ? 'yes' : 'no',
        }));
    }

    const getTimeSlotsForDate = (date) => {
        if (!workingHoursData?.getStaffAvailability?.availability) return [];
    
        // Format date in user's timezone, not just in UTC
        const formattedDate = format(date, 'yyyy-MM-dd');
    
        // Match the availability for the local date
        const dayAvailability = workingHoursData?.getStaffAvailability?.availability
            .find((day) => day.date === formattedDate);
    
        if (!dayAvailability) return [];
    
        const forceLocalClock = inferForceLocalClockForDay(dayAvailability, timeZone);

        const toMinutes = (timeLabel) => {
            const parsed = parse(String(timeLabel || ""), "h:mma", new Date());
            if (Number.isNaN(parsed.getTime())) return null;
            return parsed.getHours() * 60 + parsed.getMinutes();
        };

        const minStartGapMinutes =
            Math.max(1, Number(bookingType?.length || 0)) +
            Math.max(0, Number(bookingType?.increments || 0));
        const enforceGapLocally =
            Boolean(selectedStaffId && selectedStaffId !== "All") ||
            Number(bookingType?.assignedStaffs?.length || 0) <= 1;

        const mappedSlots = dayAvailability.timeSlots.map((slot) => {
            // Convert time to user's timezone (for display)
            const convertedTime = formatTimeFromSlotPayload(slot.time, timeZone, forceLocalClock);

            const slotLocalKey = `${formattedDate}|${String(convertedTime || "").toLowerCase()}`;
            const bookedBookingTypes = bookingData?.getBookingByBookingTypeId;

            const bookedSlots = bookedBookingTypes?.filter((booking) => {
                if (booking.status === BookingTypeStatus.DELETED) return false;
                const bookedLocalKey = toLocalDayTimeKey(booking.time, timeZone);
                return bookedLocalKey === slotLocalKey;
            });
    
            const showRemaining = bookingType?.showRemaining ?? false;
            const bookedCount = bookedSlots?.length || 0;
            const maxInvitees = bookingType?.maxInvitees || 0;
            const remainingSlots = maxInvitees - bookedCount;

            const slotStartUtc = toUtcFromLocalDayAndTime(formattedDate, convertedTime, timeZone);
            const slotDurationMin = Number(bookingType?.length || 0);
            const availableStaffIds = (slot?.availableStaff || [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id) && id > 0);
            const hasStaffSpecificSelection = selectedStaffId !== "All" && !!selectedStaffId;
            const selectedStaffNumeric = hasStaffSpecificSelection ? Number(selectedStaffId) : null;
            const candidateStaffIds =
                hasStaffSpecificSelection && selectedStaffNumeric
                    ? [selectedStaffNumeric]
                    : (availableStaffIds.length > 0 ? availableStaffIds : [null]);

            const staffWithoutConflict = candidateStaffIds.filter((staffId) =>
                !hasSlotConflict({
                    slotStartUtc,
                    slotDurationMin,
                    targetStaffId: staffId,
                    bookings: bookedBookingTypes,
                })
            );

            const hasEligibleStaff = staffWithoutConflict.length > 0;
    
            return {
                time: convertedTime,
                availableStaff: slot.availableStaff,
                eligibleStaffIds: staffWithoutConflict,
                remainingSlots: hasEligibleStaff ? remainingSlots : 0,
                showRemaining
            };
        });

        if (!enforceGapLocally || minStartGapMinutes <= 1) {
            return mappedSlots;
        }

        // Safety clamp for public booking flow: ensure slot starts respect duration + buffer.
        const ordered = [...mappedSlots].sort((a, b) => {
            const left = toMinutes(a?.time);
            const right = toMinutes(b?.time);
            return (left ?? 0) - (right ?? 0);
        });

        const filtered = [];
        let lastAcceptedStart = null;
        ordered.forEach((slot) => {
            const currentStart = toMinutes(slot?.time);
            if (currentStart == null) return;
            if (lastAcceptedStart == null || currentStart - lastAcceptedStart >= minStartGapMinutes) {
                filtered.push(slot);
                lastAcceptedStart = currentStart;
            }
        });

        return filtered;
    };
    
    // like utcToZonedTime  will adjust for you no need to do it yourself
    // function isDaylightSavingTime(localDate, timeZone) {
    //     // We'll compare offsets for Jan 1 and Jul 1 in the same year
    //     const january = utcToZonedTime(
    //         new Date(localDate.getFullYear(), 0, 1),
    //         timeZone
    //     );
    //     const july = utcToZonedTime(
    //         new Date(localDate.getFullYear(), 6, 1),
    //         timeZone
    //     );

    //     // Get the UTC offsets (in minutes) for January 1, July 1, and the current date
    //     const januaryOffset = january.getTimezoneOffset();
    //     const julyOffset = july.getTimezoneOffset();
    //     const currentOffset = localDate.getTimezoneOffset();

    //     // Typically, the "smaller" offset is the DST offset in the Northern Hemisphere
    //     // (the clock moves forward, so you get a smaller *offset* from UTC).
    //     const smallerOffset = Math.min(januaryOffset, julyOffset);

    //     // If currentOffset matches the smaller offset, it's probably DST
    //     return currentOffset === smallerOffset;
    // }

    const toLocalDayTimeKey = (value, tz) => {
        try {
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) return "";
            const dayKey = formatInTimeZone(parsed, tz, "yyyy-MM-dd");
            const timeKey = formatInTimeZone(parsed, tz, "h:mma").toLowerCase();
            return `${dayKey}|${timeKey}`;
        } catch (_error) {
            return "";
        }
    };

    const handleTimeSlotClick = (time, date) => {


        // Get available staff for this time slot
        const daySlots = getTimeSlotsForDate(date);
        const timeSlot = daySlots.find((slot) => slot.time === time);

        if (!timeSlot?.availableStaff?.length) {
            console.error("No staff available for this time slot");
            return;
        }

        const selectedAssignedStaff =
            selectedStaffId && selectedStaffId !== "All"
                ? bookingType?.assignedStaffs?.find((staff) => Number(staff.staffId) === Number(selectedStaffId))
                : null;
        const roundRobinStaffId =
            !selectedAssignedStaff
                ? pickRoundRobinStaffId(timeSlot?.eligibleStaffIds || timeSlot?.availableStaff || [])
                : null;
        const roundRobinAssignedStaff =
            roundRobinStaffId
                ? bookingType?.assignedStaffs?.find(
                    (staff) => Number(staff.staffId) === Number(roundRobinStaffId)
                )
                : null;
        const fallbackAssignedStaff =
            roundRobinAssignedStaff ||
            bookingType?.assignedStaffs?.find(
                (staff) => Number(staff.staffId) === Number(timeSlot?.availableStaff?.[0] || 0)
            ) ||
            bookingType?.assignedStaffs?.[0];
        const resolvedStaffUrl = selectedAssignedStaff?.url || fallbackAssignedStaff?.url || "";
        if (!resolvedStaffUrl) {
            console.error("No valid staff url found for booking route.", {
                selectedStaffId,
                bookingTypeId: bookingType?.id,
            });
            return;
        }

        const bookingDetails = {
            businessUrl: businessUrl,
            bookingTypeLink: bookingType.link,
            staffUrl: resolvedStaffUrl,
            time: `${format(date, "yyyy-MM-dd")}T${time}`, // Construct full time
        };
        localStorage.setItem("bookingDetails", JSON.stringify(bookingDetails));
        localStorage.setItem("timezone", timeZone?.timezone ?? timeZone);
        localStorage.setItem('bookingDisplayCalendar', displayCalendar || 'gregory');
        localStorage.setItem('bookingDisplayClock', displayClock || 'standard');
        const existingParams = new URLSearchParams(location.search || "");
        const entry = existingParams.get("entry") || "";
        if (entry) {
            sessionStorage.setItem("bookingEntry", entry);
        }
        const bookingPrefill = {
            referralLeadId: existingParams.get("referralLeadId") || "",
            coordinatorEmail: existingParams.get("coordinatorEmail") || "",
            coordinatorName: existingParams.get("coordinatorName") || "",
            email: existingParams.get("email") || "",
            name: existingParams.get("name") || "",
            phone: existingParams.get("phone") || "",
            notes: existingParams.get("notes") || "",
        };
        if (Object.values(bookingPrefill).some(Boolean)) {
            sessionStorage.setItem("bookingPrefill", JSON.stringify(bookingPrefill));
        }
        const forwardParams = new URLSearchParams();
        forwardParams.set('timezone', timeZone);
        forwardParams.set('calendar', displayCalendar || 'gregory');
        forwardParams.set('clock', displayClock || 'standard');
        [
            "entry",
            "referralLeadId",
            "coordinatorEmail",
            "coordinatorName",
            "email",
            "name",
            "phone",
            "notes",
        ].forEach((key) => {
            const value = existingParams.get(key);
            if (value) forwardParams.set(key, value);
        });
        // Navigate to the next page
        const path = `/${businessUrl}/book/${bookingDetails.staffUrl}/${bookingDetails.bookingTypeLink}/${bookingDetails.time}`;
        navigate(forwardParams.toString() ? `${path}?${forwardParams.toString()}` : path);
    };
    ;
    const isSlotDisabled = (slotTime, slotDate, timeZone) => {
        // Ensure slotDate is a Date object
        const slotDateObject = slotDate instanceof Date ? slotDate : slotDate?.date;

        // Parse the slot time into hours and minutes
        const parsedTime = parse(slotTime, 'h:mma', new Date());
        if (isNaN(parsedTime)) {
            console.error('Invalid slot time:', slotTime);
            return true; // Disable if the slot time is invalid
        }

        const hour = parsedTime.getHours();
        const minute = parsedTime.getMinutes();

        // Combine slot date and time into a Date object in the specified time zone
        const slotDateTime = new Date(
            slotDateObject.getFullYear(),
            slotDateObject.getMonth(),
            slotDateObject.getDate(),
            hour,
            minute
        );
        // Convert slotDateTime to UTC based on the specified time zone
        const slotDateTimeUtc = zonedTimeToUtc(slotDateTime, timeZone);

        // Get the current date and time in UTC
        const currentDateTimeUtc = new Date();

        // Compare dates
        return slotDateTimeUtc <= currentDateTimeUtc;

    };

    return React.cloneElement(children, {
        isMobile,
        workingHoursData,
        setSelectedDate,
        timeZone,
        userTimezone,
        isSlotDisabled,
        handleTimeSlotClick,
        isTimeSlotVisible,
        getTimeSlotsForDate,
        viewType,
        RenderButtons,
        bookingType,
        openings
    });
};

export default BookingTimeSlotWrapper;
    const slotZMode = String(process.env.REACT_APP_STAFF_AVAILABILITY_Z_MODE || "utc").toLowerCase();
